import { useEffect, useRef, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  CircularProgress,
  Typography,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DownloadIcon from "@mui/icons-material/Download";
import { renderAsync } from "docx-preview";
import { saveAs } from "file-saver";

// Import docx-preview CSS for proper styling
// The source map warning can be ignored - it's just a dev warning and doesn't affect functionality
// import "docx-preview/dist/docx-preview.css";

/**
 * DocumentPreviewModal Component
 *
 * Displays a preview of a .docx document or image in a modal dialog
 * Allows users to preview before downloading
 *
 * @param {boolean} open - Controls modal visibility
 * @param {function} onClose - Callback when modal is closed
 * @param {function} onPrevious - Callback when Previous button is clicked (optional)
 * @param {Blob} documentBlob - The document blob to preview
 * @param {string} fileName - Suggested filename for download
 * @param {string} fileType - File extension (pdf, docx, png, jpg, etc.)
 * @param {string} title - Modal title (optional)
 */
const DocumentPreviewModal = ({
  open,
  onClose,
  onPrevious,
  documentBlob,
  fileName = "document.docx",
  fileType = "docx",
  title = "Document Preview",
}) => {
  const previewContainerRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [imageUrl, setImageUrl] = useState(null);
  const [pdfUrl, setPdfUrl] = useState(null);

  const isImage = ["jpg", "jpeg", "png", "gif", "bmp", "webp"].includes(
    fileType.toLowerCase()
  );
  const isPdf = fileType.toLowerCase() === "pdf";

  // Handle Image Previews
  useEffect(() => {
    if (open && documentBlob && isImage) {
      const url = URL.createObjectURL(documentBlob);
      setImageUrl(url);

      // Cleanup
      return () => {
        URL.revokeObjectURL(url);
        setImageUrl(null);
      };
    }
  }, [open, documentBlob, isImage]);

  // Handle PDF Previews (rendered inline via an iframe)
  useEffect(() => {
    if (open && documentBlob && isPdf) {
      const url = URL.createObjectURL(documentBlob);
      setPdfUrl(url);

      // Cleanup
      return () => {
        URL.revokeObjectURL(url);
        setPdfUrl(null);
      };
    }
  }, [open, documentBlob, isPdf]);

  // Handle DOCX Previews
  useEffect(() => {
    if (!open || !documentBlob || isImage || isPdf) return;

    // Capture the ref value at the time of effect execution
    const containerElement = previewContainerRef.current;
    let timer;

    const renderDocument = async () => {
      setLoading(true);
      setError(null);

      // Store container ref locally to avoid issues with re-renders
      const container = previewContainerRef.current;

      try {
        // Clear previous content
        if (container) {
          container.innerHTML = "";
        } else {
           // If we are here but supposed to be rendering docx, it might be an issue.
           // However, if we just switched from image to docx, ref might need a moment.
           // throw new Error("Container ref is null - cannot render");
           return; 
        }

        // Verify blob
        if (!documentBlob) {
          throw new Error("Document blob is null");
        }

        // Render the document with proper options for tables and images
        await renderAsync(documentBlob, container, null, {
          className: "docx-preview",
          inWrapper: true,
          ignoreWidth: false,
          ignoreHeight: false,
          ignoreFonts: false,
          breakPages: true,
          ignoreLastRenderedPageBreak: true,
          experimental: false,
          trimXmlDeclaration: true,
          useBase64URL: true, // Important for images
          useMathMLPolyfill: false,
          renderHeaders: true,
          renderFooters: true,
          renderFootnotes: true,
          renderEndnotes: true,
          renderComments: false,
        });

        // Wait a bit for DOM to update
        await new Promise((resolve) => setTimeout(resolve, 200));

        if (container.children.length === 0) {
          // console.error("❌ No children in container!");
          throw new Error("No content was rendered");
        }
        setLoading(false);
      } catch (err) {
        // console.error("❌ ERROR:", err);
        // console.error("Stack:", err.stack);
        setError(`Failed to preview: ${err.message}`);
        setLoading(false);
      }
    };

    if (open && documentBlob) {
      // Add a small delay to ensure Dialog is fully mounted
      timer = setTimeout(() => {
        if (previewContainerRef.current) {
          renderDocument();
        } else {
          // console.log("Container ref still not available after delay");
        }
      }, 100);
    } 

    // Cleanup function to clear the container and timer when modal closes
    return () => {
      if (timer) {
        clearTimeout(timer);
      }
      // Use the captured ref value from when effect ran
      if (containerElement) {
        containerElement.innerHTML = "";
      }
    };
  }, [open, documentBlob, isImage, isPdf]);

  const handleDownload = () => {
    if (documentBlob) {
      saveAs(documentBlob, fileName);
    }
  };

  const handleClose = () => {
    setError(null);
    onClose();
  };

  const handlePrevious = () => {
    if (onPrevious) {
      onPrevious();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          height: "90vh",
          maxHeight: "90vh",
        },
      }}
    >
      <DialogTitle sx={{ m: 0, p: 2, display: "flex", alignItems: "center" }}>
        <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
          {title}
        </Typography>
        <IconButton
          aria-label="close"
          onClick={handleClose}
          sx={{
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent
        dividers
        sx={{
          p: 2,
          overflow: "auto",
          backgroundColor: "#f5f5f5",
          position: "relative",
          display: 'flex',
          justifyContent: 'center',
          alignItems: isImage ? 'center' : 'flex-start',
          p: isPdf ? 0 : 2,
        }}
      >
        {/* Loading Overlay - positioned absolutely on top */}
        {loading && (
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              backgroundColor: "rgba(255, 255, 255, 0.9)",
              zIndex: 1000,
            }}
          >
            <CircularProgress />
            <Typography variant="body1" sx={{ ml: 2 }}>
              Loading preview...
            </Typography>
          </Box>
        )}

        {/* Error Overlay - positioned absolutely on top */}
        {error && (
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              flexDirection: "column",
              backgroundColor: "rgba(255, 255, 255, 0.95)",
              zIndex: 1000,
            }}
          >
            <Typography variant="body1" color="error" gutterBottom>
              {error}
            </Typography>
            <Button
              variant="contained"
              startIcon={<DownloadIcon />}
              onClick={handleDownload}
              sx={{ mt: 2 }}
            >
              Download Document
            </Button>
          </Box>
        )}

        {/* Image Preview */}
        {isImage && imageUrl && (
           <img
             src={imageUrl}
             alt={fileName}
             style={{
               maxWidth: '100%',
               maxHeight: '100%',
               objectFit: 'contain',
               boxShadow: "0 0 10px rgba(0,0,0,0.1)"
             }}
           />
        )}

        {/* PDF Preview - rendered inline in an iframe */}
        {isPdf && pdfUrl && (
          <iframe
            src={pdfUrl}
            title={fileName}
            style={{
              width: "100%",
              height: "100%",
              border: "none",
            }}
          />
        )}

        {/* DOCX Preview Container - conditionally rendered but ref is needed */}
        {!isImage && !isPdf && (
          <Box
            ref={previewContainerRef}
            sx={{
              width: "100%",
              "backgroundColor": "white",
              "padding": "20px",
              "minHeight": "600px",
              "& .docx-wrapper": {
                backgroundColor: "white",
                padding: "30px",
                boxShadow: "0 0 10px rgba(0,0,0,0.1)",
              },
              "& .docx": {
                margin: "0 auto",
              },
              // Style tables
              "& table": {
                borderCollapse: "collapse",
                width: "100%",
                marginBottom: "1em",
              },
              "& table td, & table th": {
                border: "1px solid #ddd",
                padding: "8px",
              },
              "& table th": {
                backgroundColor: "#f2f2f2",
                fontWeight: "bold",
              },
            }}
          />
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2, gap: 1 }}>
        <Button onClick={handleClose} variant="outlined">
          Close
        </Button>
        {onPrevious && (
          <Button
            onClick={handlePrevious}
            variant="contained"
            color="secondary"
          >
            Previous
          </Button>
        )}
        <Button
          onClick={handleDownload}
          variant="contained"
          startIcon={<DownloadIcon />}
          disabled={!documentBlob}
        >
          Download
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DocumentPreviewModal;
